import moment from 'moment';
import { stringSimilarity } from 'string-similarity-js';

/**
 * AMOUNT REGEX - matches formatted numbers (1.000) and plain numbers (100, 1000, etc)
 */
const AMOUNT_REGEX =
  /(^|[^0-9A-Za-z])([+-])?\s*(?:R\$\s*)?(\d{1,3}(?:[.,]\d{3})+|\d+)([.,]\d+)?(?![0-9A-Za-z])/g;

function normalizeNumber(intPart, fracPart) {
  if (/,/.test(fracPart || '')) {
    return Number((intPart || '').replace(/\./g, '') + (fracPart || '').replace(',', '.'));
  }
  return Number((intPart || '').replace(/,/g, '') + (fracPart || ''));
}

/** BRL formatter */
const fmt = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

/** Numbers only (kept for completeness) */
const extractNumbers = (text) => {
  const nums = [];
  let m;
  while ((m = AMOUNT_REGEX.exec(text)) !== null) {
    const sign = m[2] === '-' ? -1 : 1;
    const intPart = m[3] || '';
    const fracPart = m[4] || '';
    const n = normalizeNumber(intPart, fracPart);
    if (!Number.isNaN(n)) nums.push(sign * n);
  }
  return nums;
};

/**
 * Rich extractor (unchanged): [{ token, value, start, end, label, lineIndex }]
 */
function extractEntriesWithLabels(text) {
  const lines = text.split(/\r?\n/);
  const lineOffsets = [];
  let offset = 0;
  for (const ln of lines) { lineOffsets.push(offset); offset += ln.length + 1; }

  const entries = [];

  // limpa trechos não-numéricos (remove números/cifrões/sinais e pontuação separadora)
  const clean = (s) =>
    (s || '')
      .replace(/(?<![0-9A-Za-z])(?:R\$)?\s*[+-]?\s*(?:\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]\d+)?(?![0-9A-Za-z])/g, '')
      .replace(/[-–—:+*/=•|•·•]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    if (!line) continue;

    // colete todos os matches da linha para calcular "ocorrência" e limites vizinhos
    AMOUNT_REGEX.lastIndex = 0;
    let localMatches = [];
    let m;
    while ((m = AMOUNT_REGEX.exec(line)) !== null) localMatches.push(m);
    if (!localMatches.length) continue;

    // On lines with a negative value, small unsigned positive numbers (1-2 digits)
    // are almost always label fragments (e.g. "cartão 2", "fatura 02", "home 10"),
    // not financial amounts. Filter them out.
    const hasNegative = localMatches.some(match => match[2] === '-');
    if (hasNegative) {
      localMatches = localMatches.filter(match => {
        if (match[2] === '-' || match[2] === '+') return true;
        if (/R\$/.test(match[0])) return true;
        if (match[4]) return true; // has decimal part → real amount
        const rawDigits = (match[3] || '').replace(/[.,]/g, '');
        return rawDigits.length > 2;
      });
      if (!localMatches.length) continue;
    }

    for (let k = 0; k < localMatches.length; k++) {
      const match = localMatches[k];

      // valor numérico
      const sign = match[2] === '-' ? -1 : 1;
      const intPart = match[3] || '';
      const fracPart = match[4] || '';
      const numeric = normalizeNumber(intPart, fracPart);
      if (Number.isNaN(numeric)) continue;
      const value = sign * numeric;

      // span local do número atual
      const localStart = match.index + (match[1] ? match[1].length : 0);
      const lead = line.slice(localStart);
      const numLike = lead.match(/^\s*(?:R\$\s*)?[+-]?\s*[0-9][0-9.,]*/)?.[0] || '';
      const localEnd = localStart + numLike.length;

      // spans do vizinho anterior/próximo (para extrair "texto entre números")
      const prev = localMatches[k - 1];
      const next = localMatches[k + 1];

      const prevLocalEnd = prev
        ? prev.index + (prev[1] ? prev[1].length : 0) +
          (line.slice(prev.index + (prev[1] ? prev[1].length : 0))
            .match(/^\s*(?:R\$\s*)?[+-]?\s*[0-9][0-9.,]*/)?.[0]?.length || 0)
        : 0;

      const nextLocalStart = next
        ? next.index + (next[1] ? next[1].length : 0)
        : line.length;

      // candidatos de rótulo
      // 1) texto ENTRE este número e o próximo (preferência)
      const betweenRaw = line.slice(localEnd, nextLocalStart);
      const between = clean(betweenRaw);

      // 2) fallback: texto entre o fim do número anterior e o início deste
      const beforeRaw = line.slice(prevLocalEnd, localStart);
      const before = clean(beforeRaw);

      // 3) fallback final: texto depois ou antes do número (antigo comportamento)
      const afterRaw = line.slice(localEnd);
      const after = clean(afterRaw);

      let label = '';
      if (before) label = before;         // preferido: "label valor"
      else if (between) label = between;  // ex: "valor label"
      else if (after)  label = after;     // último recurso

      // posições globais
      const globalStart = lineOffsets[lineIndex] + localStart;
      const globalEnd = lineOffsets[lineIndex] + localEnd;

      // metadados para reconciliação futura (se você já os usa)
      const intAbs = Math.trunc(Math.abs(value));
      const lineHash = line
        .replace(/(?:R\$)?\s*[+-]?\s*(?:\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]\d+)?/g, '##')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const token = `${lineIndex}|${localStart}|${value}|${label.toLowerCase()}`;

      entries.push({
        id: token,           // mantemos por compat; o ID estável vem da reconciliação
        token,
        value,
        start: globalStart,
        end: globalEnd,
        label,
        lineIndex,
        occurrence: k,
        lineHash,
        intPart: intAbs
      });
    }
  }

  return entries;
}

/** Date helpers (unchanged) */
const fmtDate = (iso) => {
  if (!iso) return '–';
  const m = moment(iso, 'YYYY-MM-DD', true);
  return m.isValid() ? m.format('D/M/YYYY') : '–';
};
const daysLeft = (iso) => {
  if (!iso) return null;
  const d = moment(iso, 'YYYY-MM-DD', true);
  if (!d.isValid()) return null;

  // Check if due date is on weekend (Saturday=6, Sunday=0)
  const dayOfWeek = d.day();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  if (isWeekend) {
    // Adjust to next Monday
    const adjustedDate = d.clone();
    if (dayOfWeek === 0) {
      // Sunday -> add 1 day to get Monday
      adjustedDate.add(1, 'days');
    } else {
      // Saturday -> add 2 days to get Monday
      adjustedDate.add(2, 'days');
    }

    const daysToAdjusted = adjustedDate.startOf('day').diff(moment().startOf('day'), 'days');
    return { days: daysToAdjusted, adjusted: true };
  }

  return { days: d.startOf('day').diff(moment().startOf('day'), 'days'), adjusted: false };
};

/** Build highlight ranges — badge classes are applied separately by the badge effect */
const buildHighlightRanges = (entries) => {
  const negs = entries
    .filter(e => e.value < 0)
    .map(e => ({
      highlight: [e.start, e.end],
      className: e.is_paid ? 'red isPaid' : 'red'
    }));

  const pos = entries
    .filter(e => e.value >= 0)
    .map(e => ({ highlight: [e.start, e.end], className: 'green' }));

  return [...negs, ...pos];
};

/* ------------ NEW: Stable IDs + reconciliation ------------- */

/** Short random ID (content-independent) */
const generateId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().slice(0, 8);
    }
  } catch {}
  return (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)).slice(0, 8);
};

/**
 * Detect bulk line shift when lines are added/removed at the top.
 * Returns the most common lineIndex offset between raw and indexed entries.
 */
function detectLineShift(rawEntries, indexedEntries) {
  const offsets = {};
  for (const raw of rawEntries) {
    for (const prev of indexedEntries) {
      const rawLabel = (raw.label || '').toLowerCase();
      const prevLabel = (prev.label || '').toLowerCase();
      const labelSim = rawLabel && prevLabel ? stringSimilarity(rawLabel, prevLabel) : 0;
      const valueMatches = Math.trunc(Math.abs(raw.value)) === Math.trunc(Math.abs(prev.value));
      if (labelSim > 0.8 || (valueMatches && labelSim > 0.5)) {
        const offset = raw.lineIndex - prev.lineIndex;
        offsets[offset] = (offsets[offset] || 0) + 1;
      }
    }
  }
  let bestOffset = 0;
  let bestCount = 0;
  for (const [offset, count] of Object.entries(offsets)) {
    if (count > bestCount) {
      bestCount = count;
      bestOffset = Number(offset);
    }
  }
  return bestOffset;
}

/**
 * Calculate matching score between raw entry and previous entry
 * Higher score means better match
 *
 * Strategy: Prioritize what DIDN'T change
 * - If label matches (fuzzy), it's likely the same entry (even if value changed)
 * - If value matches, it's likely the same entry (even if label changed)
 * - If on same line with metadata, very likely same entry (preserve metadata!)
 */
function calculateMatchScore(rawEntry, prevEntry, lineShift = 0) {
  let score = 0;

  // Check if previous entry has metadata (metadataId or customName)
  const hasMetadata = !!(prevEntry.metadataId || prevEntry.customName);

  // Position match - account for bulk line shift (e.g. lines added at top)
  const positionMatches = rawEntry.lineIndex === (prevEntry.lineIndex + lineShift);
  const distance = positionMatches ? Math.abs(rawEntry.start - prevEntry.start) : 999;

  // Label similarity using string-similarity-js (0-1 range)
  const rawLabel = (rawEntry.label || '').toLowerCase().trim();
  const prevLabel = (prevEntry.label || '').toLowerCase().trim();

  let labelSimilarity = 0;
  if (rawLabel && prevLabel) {
    labelSimilarity = stringSimilarity(rawLabel, prevLabel);
    if (positionMatches) {
      score += labelSimilarity * 80; // Same line: full weight
    } else {
      score += labelSimilarity * 50; // Different line: reduced weight
    }
  }

  // Position boost for metadata entries — only when label is similar enough.
  // Without the label guard, a completely different entry at the same line would
  // inherit the metadataId just because the user rewrote that line.
  if (hasMetadata && positionMatches && distance <= 50 && labelSimilarity >= 0.4) {
    score += 100;
  }

  // Value match
  const rawValue = Math.trunc(Math.abs(rawEntry.value));
  const prevValue = Math.trunc(Math.abs(prevEntry.value));
  const valueMatches = rawValue === prevValue;

  if (valueMatches && positionMatches) {
    // Same value + same line
    score += 60;
  } else if (valueMatches) {
    // Same value but different line
    score += 30;
  }

  // Additional position bonus (prefer closer matches within same line)
  if (positionMatches) {
    score += distance <= 5 ? 15 : distance <= 20 ? 10 : distance <= 50 ? 5 : 0;
  }

  return score;
}

/**
 * Find the best matching previous entry for a raw entry
 */
function findBestMatch(rawEntry, indexedEntries, usedIds, lineShift = 0) {
  let bestMatch = null;
  let bestScore = -1;

  for (const prevEntry of indexedEntries) {
    if (usedIds.has(prevEntry.id)) continue;

    const score = calculateMatchScore(rawEntry, prevEntry, lineShift);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = prevEntry;
    }
  }

  // Require minimum score threshold for match
  // Lower threshold if best match has metadata (more forgiving to preserve metadata)
  const hasMetadata = bestMatch && (bestMatch.metadataId || bestMatch.customName);
  const MATCH_THRESHOLD = hasMetadata ? 50 : 70;
  return bestScore >= MATCH_THRESHOLD ? bestMatch : null;
}

/**
 * Create or update entry index record
 * Preserves metadata fields (metadataId, customName, kind) if they exist
 */
function createIndexRecord(entry, existingIndexRecord) {
  const record = {
    lineIndex: entry.lineIndex,
    start: entry.start,
    label: entry.label,
    value: entry.value
  };

  // Preserve metadata fields from existing record if they exist
  if (existingIndexRecord) {
    if (existingIndexRecord.metadataId) {
      record.metadataId = existingIndexRecord.metadataId;
    }
    if (existingIndexRecord.customName) {
      record.customName = existingIndexRecord.customName;
    }
    if (existingIndexRecord.kind) {
      record.kind = existingIndexRecord.kind;
    }
  }

  return record;
}

/**
 * Migrate payment data from old format to new format
 * Handles both object and boolean formats
 */
function migratePaymentData(entryId, token, oldPayments) {
  const defaultPayment = {
    is_paid: false,
    due_date: '',
    paid_at: null,
    // Which instrument settles this entry (a paymentSources id), and whether the
    // line is only a reminder. Both keep the entry out of the cash totals when
    // set — see the excluded* getters in NotesStore.
    paymentSourceId: null,
    reminderOnly: false,
    attachments: [],
    created_at: null,
    updated_at: null,
    history: []
  };

  // Check for payment by ID first
  const paymentById = oldPayments[entryId];
  if (paymentById !== undefined) {
    if (typeof paymentById === 'object') {
      return {
        is_paid: !!paymentById.is_paid,
        due_date: paymentById.due_date || '',
        paid_at: paymentById.paid_at || null,
        paymentSourceId: paymentById.paymentSourceId || null,
        reminderOnly: paymentById.reminderOnly === true,
        attachments: paymentById.attachments || [],
        created_at: paymentById.created_at || null,
        updated_at: paymentById.updated_at || null,
        history: paymentById.history || []
      };
    }
    if (typeof paymentById === 'boolean') {
      return { ...defaultPayment, is_paid: paymentById };
    }
  }

  // Fallback to token-based payment
  const paymentByToken = oldPayments[token];
  if (paymentByToken !== undefined) {
    if (typeof paymentByToken === 'object') {
      return {
        is_paid: !!paymentByToken.is_paid,
        due_date: paymentByToken.due_date || '',
        paid_at: paymentByToken.paid_at || null,
        paymentSourceId: paymentByToken.paymentSourceId || null,
        reminderOnly: paymentByToken.reminderOnly === true,
        attachments: paymentByToken.attachments || [],
        created_at: paymentByToken.created_at || null,
        updated_at: paymentByToken.updated_at || null,
        history: paymentByToken.history || []
      };
    }
    if (typeof paymentByToken === 'boolean') {
      return { ...defaultPayment, is_paid: paymentByToken };
    }
  }

  return defaultPayment;
}

/**
 * Reconcile raw entries to stable IDs.
 * - note.entryIndex: { [id]: { lineIndex, start, label, value } }
 * - note.payments:   { [id]: { is_paid, due_date, paymentSourceId, reminderOnly, attachments } }
 *
 * Returns:
 * { entries: [{...raw, id, is_paid, due_date, paymentSourceId, reminderOnly, attachments }], entryIndex, payments }
 */
function reconcileEntries(rawEntries, note) {
  const entryIndex = { ...(note.entryIndex || {}) };
  const oldPayments = { ...(note.payments || {}) };

  // Convert existing index to array for easier processing
  const indexedEntries = Object.entries(entryIndex)
    .map(([id, snapshot]) => ({ id, ...snapshot }));

  const usedIds = new Set();
  const tokenToId = {}; // Maps tokens to IDs for migration

  // Detect bulk line shift (e.g. lines added/removed at the top)
  const lineShift = detectLineShift(rawEntries, indexedEntries);

  // Process each raw entry
  const resolvedEntries = rawEntries.map(rawEntry => {
    // Try to find a matching previous entry
    const bestMatch = findBestMatch(rawEntry, indexedEntries, usedIds, lineShift);

    let entryId;
    let existingIndexRecord = null;
    if (bestMatch) {
      // Reuse existing ID
      entryId = bestMatch.id;
      usedIds.add(bestMatch.id);
      existingIndexRecord = entryIndex[entryId];
    } else {
      // Generate new ID
      entryId = generateId();
      indexedEntries.push({ id: entryId, ...createIndexRecord(rawEntry) });
    }

    // Update entry index (preserving metadata fields)
    entryIndex[entryId] = createIndexRecord(rawEntry, existingIndexRecord);

    // Track token to ID mapping
    tokenToId[rawEntry.token] = entryId;

    return { ...rawEntry, id: entryId };
  });

  // Build payments object with migrated data
  const payments = {};
  for (const entry of resolvedEntries) {
    payments[entry.id] = migratePaymentData(
      entry.id,
      entry.token,
      oldPayments
    );
  }

  // Add payment data and metadata fields to entries
  const entries = resolvedEntries.map(entry => {
    const indexRecord = entryIndex[entry.id];
    const paymentData = payments[entry.id];

    const fullEntry = {
      ...entry,
      is_paid: paymentData.is_paid,
      due_date: paymentData.due_date,
      paid_at: paymentData.paid_at,
      paymentSourceId: paymentData.paymentSourceId,
      reminderOnly: paymentData.reminderOnly,
      attachments: paymentData.attachments,
      created_at: paymentData.created_at,
      updated_at: paymentData.updated_at,
      history: paymentData.history
    };

    // Add metadata fields from entryIndex
    if (indexRecord.metadataId) {
      fullEntry.metadataId = indexRecord.metadataId;
    }
    if (indexRecord.customName) {
      fullEntry.customName = indexRecord.customName;
    }
    if (indexRecord.kind) {
      fullEntry.kind = indexRecord.kind;
    }

    return fullEntry;
  });

  // Clean up ghost entries that no longer exist in the current content
  const activeIds = new Set(resolvedEntries.map(e => e.id));

  // Remove ghost entries from entryIndex
  const cleanedEntryIndex = {};
  for (const [id, record] of Object.entries(entryIndex)) {
    if (activeIds.has(id)) {
      cleanedEntryIndex[id] = record;
    }
  }

  // Remove ghost entries from payments
  const cleanedPayments = {};
  for (const [id, paymentData] of Object.entries(payments)) {
    if (activeIds.has(id)) {
      cleanedPayments[id] = paymentData;
    }
  }

  return { entries, entryIndex: cleanedEntryIndex, payments: cleanedPayments };
}

/**
 * Normalize userId by removing "user:" prefix if present.
 * Keeps "guest:" prefix intact.
 */
function normalizeUserId(userId) {
  if (!userId) return userId;

  // If it starts with "user:" and is not a guest, remove the prefix
  if (userId.startsWith('user:') && !userId.startsWith('guest:')) {
    return userId.replace(/^user:/, '');
  }

  return userId;
}

export {
  AMOUNT_REGEX,
  extractNumbers,
  extractEntriesWithLabels,
  fmt,
  fmtDate,
  daysLeft,
  buildHighlightRanges,
  generateId,
  reconcileEntries,
  normalizeUserId
};
